import { useState, useCallback, Suspense, lazy } from "react";
import { Loader2 } from "lucide-react";
import CodeEditor from "./CodeEditor";
import { isBlockMode } from "../lib/modes";
import { useMode } from "../context/ModeContext";
import { clearDraft } from "../lib/blocksDraft";

const BlocksWorkspace = lazy(() => import("./blocks/BlocksWorkspace").then((m) => ({ default: m.BlocksWorkspace })));
const BlocksPythonPanel = lazy(() => import("./blocks/BlocksWorkspace").then((m) => ({ default: m.BlocksPythonPanel })));
const NavWorkspace = lazy(() => import("./nav/NavWorkspace").then((m) => ({ default: m.NavWorkspace })));

// Chooses the editor by the question's mode: Blocks questions get the Blockly workspace
// (with run/submit driven by the generated Python), Python questions get the text editor.
// layout="wide": full-width stacked page (question above) - taller canvas, Python mirror beside the run/submit panel.
export default function CodeWorkspace({ question, onSaveAsSolution, layout = "split", onCorrect, ...rest }) {
  const isBlocks = isBlockMode(question?.mode);
  const draftOwner = useMode()?.draftOwner;
  const [pythonCode, setPythonCode] = useState("");
  const [workspace, setWorkspace] = useState(null);
  const [textMode, setTextMode] = useState(false);
  const [handoffCode, setHandoffCode] = useState(null);

  // A solved question starts fresh next time - the saved draft would otherwise hand the answer back
  const handleCorrect = useCallback((...args) => {
    clearDraft(question?.question_id, draftOwner);
    onCorrect?.(...args);
  }, [question?.question_id, draftOwner, onCorrect]);
  const editorProps = { ...rest, onCorrect: isBlocks ? handleCorrect : onCorrect };

  const handleBlocksChange = useCallback((code, state) => {
    setPythonCode(code);
    setWorkspace(state);
  }, []);

  if (!isBlocks) return <CodeEditor question={question} {...editorProps} />;

  if (question.mode === "junior" || question.blocks?.kind === "nav") {
    return (
      <Suspense fallback={<div className="flex items-center justify-center h-64 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>}>
        <NavWorkspace question={question} editorProps={editorProps} />
      </Suspense>
    );
  }

  if (textMode) {
    return (
      <div className="space-y-2" data-testid="blocks-text-mode">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Editing the Python from your blocks. Still counts for the Blocks track.</span>
          <button className="text-primary hover:underline" onClick={() => setTextMode(false)} data-testid="blocks-back-to-blocks-btn">
            Back to blocks
          </button>
        </div>
        <CodeEditor question={question} {...editorProps} initialCode={handoffCode} extraPayload={{ workspace }} />
      </div>
    );
  }

  const wide = layout === "wide";
  const continueInPython = (code) => { setHandoffCode(code); setTextMode(true); };
  return (
    <div className="space-y-4" data-testid={wide ? "blocks-layout-wide" : "blocks-layout-split"}>
      <Suspense fallback={<div className={`${wide ? "h-[520px]" : "h-[420px]"} rounded-xl border border-border flex items-center justify-center text-muted-foreground`}><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading blocks…</div>}>
        <BlocksWorkspace
          questionId={question.question_id}
          inputs={question.blocks?.inputs || []}
          rules={question.blocks || null}
          starterWorkspace={question.blocks?.starter_workspace || null}
          height={wide ? 520 : 420}
          hidePythonPanel={wide}
          onChange={handleBlocksChange}
          onContinueInPython={continueInPython}
          onSaveAsSolution={onSaveAsSolution}
        />
      </Suspense>
      {wide ? (
        <div className="grid lg:grid-cols-2 gap-4 items-start">
          <Suspense fallback={null}>
            <BlocksPythonPanel code={pythonCode} onContinueInPython={continueInPython} height="280px" />
          </Suspense>
          <CodeEditor question={question} {...editorProps} hideEditor externalCode={pythonCode} extraPayload={{ workspace }} />
        </div>
      ) : (
        <CodeEditor question={question} {...editorProps} hideEditor externalCode={pythonCode} extraPayload={{ workspace }} />
      )}
    </div>
  );
}
